/**
 * @typedef {object} RhythmDeps
 * @property {any} el
 * @property {{
 * logLine: (msg: string) => void,
 * renderRhythmString: (rhythmString: string, steps: number) => void,
 * highlightRhythmStep: (step: number) => void
 * }} ui
 * @property {{
 * beatsPerBar: number,
 * subdivision: number,
 * secondaryPulses: number,
 * secondaryRounds: number,
 * secondaryMode: number,
 * primaryPulses: number,
 * primaryRounds: number,
 * primaryMode: number,
 * primaryStressVolPct: number,
 * secondaryStressVolPct: number,
 * unstressedVolPct: number
 * }} state
 * @property {(x: number, min: number, max: number) => number} clamp
 * @property {(steps: number, pulses: number) => number} maxRoundCount
 * @property {(steps: number, pulses: number, numRounds?: number) => number} modeCount
 * @property {(steps: number, pulses: number) => number} reverseOffset
 * @property {(n: number, m: number) => number} mod
 * @property {(steps: number, secondaryPulses: number, secondaryMode: number, primaryPulses: number, primaryMode: number, secondaryRounds?: number, primaryRounds?: number) => number[]} buildStressLevels
 * @property {() => number | null} getLastStepInBar
 * @property {(levels: number[]) => void} setStressLevels
 */

/** @param {RhythmDeps} deps */
export function createRhythm({
    el,
    ui,
    state,
    clamp,
    maxRoundCount,
    modeCount,
    reverseOffset,
    mod,
    buildStressLevels,
    getLastStepInBar,
    setStressLevels,
}) {
    let lastEuclidLog = '';
    let prevSecondaryPulses = state.secondaryPulses | 0;
    let prevPrimaryPulses = state.primaryPulses | 0;
    let prevPrimarySteps = Math.max(0, state.secondaryPulses | 0);
    let prevGridSteps = (state.beatsPerBar | 0) * (state.subdivision | 0);

    function stepsPerBar() {
        return state.beatsPerBar * state.subdivision;
    }

    /** @param {{ steps: number, pulses: number, rounds: number, mode: number, resetRounds: boolean }} param0 */
    function normalizePulseModeInputs({ steps, pulses, rounds, mode, resetRounds }) {
        const safeSteps = Math.max(0, steps | 0);

        if (safeSteps === 0) {
            return { pulses: 0, rounds: 0, mode: 0, modes: 1, maxRounds: 0 };
        }
        const normalizedPulses = clamp(pulses | 0, 0, safeSteps);
        const roundsMax = maxRoundCount(safeSteps, normalizedPulses);
        const normalizedRounds = resetRounds
            ? roundsMax
            : clamp(rounds | 0, 0, Math.max(0, roundsMax));
        const modes = modeCount(Math.max(1, safeSteps), normalizedPulses, normalizedRounds);
        const normalizedMode = clamp(mode | 0, 0, Math.max(0, modes - 1));

        return {
            pulses: normalizedPulses,
            rounds: normalizedRounds,
            mode: normalizedMode,
            modes,
            maxRounds: roundsMax,
        };
    }

    /** @param {{ steps: number, pulses: number, rounds: number, mode: number, modes: number, maxRounds: number }} param0 */
    function syncSecondaryPulseModeUI({ steps, pulses, rounds, mode, modes, maxRounds }) {
        if (el.secondaryPulsesNumber.value !== String(pulses))
            el.secondaryPulsesNumber.value = String(pulses);
        if (el.secondaryRoundsNumber.value !== String(rounds))
            el.secondaryRoundsNumber.value = String(rounds);
        const modeDisplay = mode + 1;
        if (el.secondaryModeNumber.value !== String(modeDisplay))
            el.secondaryModeNumber.value = String(modeDisplay);
        el.secondaryPulsesNumber.max = String(Math.max(0, steps | 0));
        el.secondaryPulsesNumber.min = '0';
        el.secondaryRoundsNumber.max = String(Math.max(0, maxRounds | 0));
        el.secondaryRoundsNumber.min = '0';
        el.secondaryModeNumber.max = String(Math.max(1, modes | 0));
        el.secondaryModeNumber.min = '1';
    }

    /** @param {{ steps: number, pulses: number, rounds: number, mode: number, modes: number, maxRounds: number }} param0 */
    function syncPrimaryPulseModeUI({ steps, pulses, rounds, mode, modes, maxRounds }) {
        if (el.primaryPulsesNumber.value !== String(pulses))
            el.primaryPulsesNumber.value = String(pulses);
        if (el.primaryRoundsNumber.value !== String(rounds))
            el.primaryRoundsNumber.value = String(rounds);
        const modeDisplay = mode + 1;
        if (el.primaryModeNumber.value !== String(modeDisplay))
            el.primaryModeNumber.value = String(modeDisplay);
        el.primaryPulsesNumber.max = String(Math.max(0, steps | 0));
        el.primaryPulsesNumber.min = '0';
        el.primaryRoundsNumber.max = String(Math.max(0, maxRounds | 0));
        el.primaryRoundsNumber.min = '0';
        el.primaryModeNumber.max = String(Math.max(1, modes | 0));
        el.primaryModeNumber.min = '1';
    }

    function updateRhythm() {
        const gridSteps = stepsPerBar();
        const secondaryContextChanged =
            (state.secondaryPulses | 0) !== prevSecondaryPulses || gridSteps !== prevGridSteps;

        const nSec = normalizePulseModeInputs({
            steps: gridSteps,
            pulses: state.secondaryPulses,
            rounds: state.secondaryRounds,
            mode: state.secondaryMode,
            resetRounds: secondaryContextChanged,
        });
        state.secondaryPulses = nSec.pulses;
        state.secondaryRounds = nSec.rounds;
        state.secondaryMode = nSec.mode;

        syncSecondaryPulseModeUI({
            steps: gridSteps,
            pulses: state.secondaryPulses,
            rounds: state.secondaryRounds,
            mode: state.secondaryMode,
            modes: nSec.modes,
            maxRounds: nSec.maxRounds,
        });

        const primarySteps = Math.max(0, state.secondaryPulses);
        const primaryPulsesChanged =
            (state.primaryPulses | 0) !== prevPrimaryPulses || primarySteps !== prevPrimarySteps;
        const nPri = normalizePulseModeInputs({
            steps: primarySteps,
            pulses: state.primaryPulses,
            rounds: state.primaryRounds,
            mode: state.primaryMode,
            resetRounds: primaryPulsesChanged,
        });
        state.primaryPulses = nPri.pulses;
        state.primaryRounds = nPri.rounds;
        state.primaryMode = nPri.mode;

        syncPrimaryPulseModeUI({
            steps: primarySteps,
            pulses: state.primaryPulses,
            rounds: state.primaryRounds,
            mode: state.primaryMode,
            modes: nPri.modes,
            maxRounds: nPri.maxRounds,
        });

        const levels = buildStressLevels(
            gridSteps,
            state.secondaryPulses,
            state.secondaryMode,
            state.primaryPulses,
            state.primaryMode,
            state.secondaryRounds,
            state.primaryRounds,
        );
        setStressLevels(levels);

        const rhythmString = levels.map((lv) => (lv === 2 ? 'X' : lv === 1 ? 'x' : '.')).join(' ');

        ui.renderRhythmString(rhythmString, stepsPerBar());
        const lastStep = getLastStepInBar();
        if (typeof lastStep === 'number' && Number.isInteger(lastStep)) {
            ui.highlightRhythmStep(lastStep);
        }

        const logStr =
            `Rhythm: steps=${gridSteps} secondaryPulses=${state.secondaryPulses} secondaryRounds=${state.secondaryRounds} secondaryMode=${state.secondaryMode} ` +
            `primaryPulses=${state.primaryPulses} primaryRounds=${state.primaryRounds} primaryMode=${state.primaryMode} ` +
            `primaryVol=${state.primaryStressVolPct}% secondaryVol=${state.secondaryStressVolPct}% unstressedVol=${state.unstressedVolPct}%`;

        if (logStr !== lastEuclidLog) {
            ui.logLine(logStr);
            lastEuclidLog = logStr;
        }

        prevSecondaryPulses = state.secondaryPulses | 0;
        prevPrimaryPulses = state.primaryPulses | 0;
        prevPrimarySteps = primarySteps;
        prevGridSteps = gridSteps;
    }

    function applyReverse() {
        const steps = stepsPerBar();

        const m1 = modeCount(steps, state.secondaryPulses, state.secondaryRounds);
        const k1 = reverseOffset(steps, state.secondaryPulses);
        state.secondaryMode = mod(k1 - state.secondaryMode, Math.max(1, m1));

        const p1 = state.secondaryPulses;
        const m2 = modeCount(p1, state.primaryPulses, state.primaryRounds);
        const k2 = reverseOffset(p1, state.primaryPulses);
        state.primaryMode = mod(k2 - state.primaryMode, Math.max(1, m2));

        updateRhythm();
        el.secondaryPulsesNumber.value = String(state.secondaryPulses);
        el.secondaryRoundsNumber.value = String(state.secondaryRounds);
        el.secondaryModeNumber.value = String(state.secondaryMode + 1);
        el.primaryPulsesNumber.value = String(state.primaryPulses);
        el.primaryRoundsNumber.value = String(state.primaryRounds);
        el.primaryModeNumber.value = String(state.primaryMode + 1);
    }

    function applyFlip() {
        const steps = stepsPerBar();

        const m1 = modeCount(steps, state.secondaryPulses, state.secondaryRounds);
        const half1 = Math.floor(m1 / 2);
        state.secondaryMode = mod(state.secondaryMode + half1, Math.max(1, m1));

        const p1 = state.secondaryPulses;
        const m2 = modeCount(p1, state.primaryPulses, state.primaryRounds);
        const half2 = Math.floor(m2 / 2);
        state.primaryMode = mod(state.primaryMode + half2, Math.max(1, m2));

        updateRhythm();
        el.secondaryPulsesNumber.value = String(state.secondaryPulses);
        el.secondaryRoundsNumber.value = String(state.secondaryRounds);
        el.secondaryModeNumber.value = String(state.secondaryMode + 1);
        el.primaryPulsesNumber.value = String(state.primaryPulses);
        el.primaryRoundsNumber.value = String(state.primaryRounds);
        el.primaryModeNumber.value = String(state.primaryMode + 1);
    }

    function applyInvertSecondary() {
        const steps = stepsPerBar();

        state.secondaryPulses = steps - state.secondaryPulses;
        const p1 = state.secondaryPulses;
        state.primaryPulses = clamp(state.primaryPulses, 0, Math.max(0, p1));

        state.secondaryMode = clamp(
            state.secondaryMode,
            0,
            Math.max(0, modeCount(steps, state.secondaryPulses, state.secondaryRounds) - 1),
        );
        state.primaryMode = clamp(
            state.primaryMode,
            0,
            Math.max(0, modeCount(p1, state.primaryPulses, state.primaryRounds) - 1),
        );

        updateRhythm();
        el.secondaryPulsesNumber.value = String(state.secondaryPulses);
        el.secondaryRoundsNumber.value = String(state.secondaryRounds);
        el.secondaryModeNumber.value = String(state.secondaryMode + 1);
        el.primaryPulsesNumber.value = String(state.primaryPulses);
        el.primaryRoundsNumber.value = String(state.primaryRounds);
        el.primaryModeNumber.value = String(state.primaryMode + 1);
    }

    return {
        stepsPerBar,
        updateRhythm,
        applyReverse,
        applyFlip,
        applyInvertSecondary,
    };
}
